import {
  CHART_COLORS, CHART_FONTS,
  CHART_STYLES,
  HighchartsOptions,
} from 'src/app/domain/shared/component/pie-chart-simple/highcharts-common/highcharts-global-const';
import {
  MaByIndustryChartsOptions,
  MaDilutionChart,
  MaDpiByVintageChart,
  MaNumberOfDealsPerIndustryChartDTO,
  MaNumOfDealsByIndustryChartsOptions,
  MaNumOfDealsChart,
  MaPcntPieChart,
  MaRaisedByIndustryChartDTO,
  MaRaisedByIndustryChartsOptions,
  MaTopFiveIndChartsSeriesDTO,
  MaTopFiveIndustriesChartDTO,
  MaTopFiveIndustriesChartsOptions,
} from '../dto/MaByIndustryChartDTO';

export class MaChartService {

  public static createByIndustryChartOptions(
    raisedPerIndustry: MaRaisedByIndustryChartDTO,
    dilution: MaDilutionChart,
    numberOfDeals: MaNumOfDealsChart,
    numberOfDealsPerIndustry: MaNumberOfDealsPerIndustryChartDTO,
    topFiveIndustries: MaTopFiveIndustriesChartDTO,
  ): MaByIndustryChartsOptions {
    const raisedByIndustryOptions = this.createRaisedByIndustryChartOptions(raisedPerIndustry);
    const numberOfDealsPerIndustryOptions = this.createNumOfDealsPerIndustryChartOptions(numberOfDealsPerIndustry);
    const dilutionOptions = this.createDilutionChartOptions(dilution);
    const numberOfDealsOptions = this.createNumberOfDealsChartOptions(numberOfDeals);
    const topFiveIndustriesOptions = this.createTopFiveIndustriesChartOptions(topFiveIndustries);

    return {
      raisedByIndustry: raisedByIndustryOptions,
      dilution: dilutionOptions,
      numberOfDeals: numberOfDealsOptions,
      numberOfDealsPerIndustry: numberOfDealsPerIndustryOptions,
      topFiveIndustries: topFiveIndustriesOptions,
    };
  }


  public static createRaisedByIndustryChartOptions(chartData: MaRaisedByIndustryChartDTO): MaRaisedByIndustryChartsOptions {
    const year1 = this.getPieChartOptions(chartData.totalRaisedYear1);
    const year2 = this.getPieChartOptions(chartData.totalRaisedYear2);

    return { year1, year2 };
  }

  public static createNumOfDealsPerIndustryChartOptions(
    chartData: MaNumberOfDealsPerIndustryChartDTO,
  ): MaNumOfDealsByIndustryChartsOptions {
    const year1 = this.getPieChartOptions(chartData.year1);
    const year2 = this.getPieChartOptions(chartData.year2);
    return { year1, year2 };
  }


  public static createDilutionChartOptions(data: MaDilutionChart): Partial<HighchartsOptions> {
    return {
      xAxis: {
        type: 'category',
        categories: data.upToPost,
        crosshair: false,
        labels: CHART_STYLES.xAxisLabelsStyle,
      },
      yAxis: [
        // Primary Y axis for dilution in %
        {
          min: 0,
          title: {
            text: undefined,
          },
          labels: CHART_STYLES.yAxisLabelsStyleWithPcnt,
        },
        // Secondary Y axis for averages (avgRound, avgPost)
        {
          title: {
            text: undefined,
          },
          labels: CHART_STYLES.yAxisLabelsStyleWithSuffix,
          opposite: true,
        },
      ],
      tooltip: CHART_STYLES.maDilutionTooltip,
      series: [
        {
          name: 'Avg Round',
          type: 'column',
          data: data.avgRound,
          yAxis: 1,
          color: CHART_COLORS.main8,
        },
        {
          name: 'Avg pre-money',
          type: 'column',
          data: data.avgPre,
          yAxis: 1,
          color: CHART_COLORS.black10,
        },
        {
          name: 'Dilution (%)',
          type: 'line',
          data: data.dilution,
          color: CHART_COLORS.orange,
          marker: {
            enabled: false,
          },
          dataLabels: {
            enabled: false,
          },
        },
      ],
    };
  }

  public static createNumberOfDealsChartOptions(data: MaNumOfDealsChart): Partial<HighchartsOptions> {
    return {
      xAxis: {
        type: 'category',
        categories: data.periods,
        crosshair: false,
        labels: CHART_STYLES.xAxisLabelsStyle,
      },
      yAxis: [
        {
          min: 0,
          title: {
            text: undefined,
          },
          labels: CHART_STYLES.yAxisLabelsStyleWithPcnt,
          stackLabels: CHART_STYLES.grandTotalStackLabels({ decimals: 0, includePercentage: true, includeSuffix: false }),
        },
      ],
      tooltip: CHART_STYLES.maNumberOfDealsTooltip,
      legend: {
        ...CHART_STYLES.legend(),
        reversed: true,
      },
      series: [
        {
          name: '> 150m',
          type: 'column',
          data: data.group4,
          color: CHART_COLORS.orange,
        },
        {
          name: '< 150m',
          type: 'column',
          data: data.group3,
          color: CHART_COLORS.green,
        },
        {
          name: '< 75m',
          type: 'column',
          data: data.group2,
          color: CHART_COLORS.main8,
        },
        {
          name: '< 25m',
          type: 'column',
          data: data.group1,
          color: CHART_COLORS.black10,
        },
      ],
    };
  }


  public static createDpiChartOptions(data: MaDpiByVintageChart): Partial<HighchartsOptions> {
    return {
      xAxis: {
        type: 'category',
        categories: data.vintageYears,
        crosshair: false,
        labels: CHART_STYLES.xAxisLabelsStyle,
      },
      yAxis: [
        {
          // Primary Y axis for percentage of funds with DPI
          min: 0,
          title: {
            text: '% of funds with DPI',
            style: { color: CHART_COLORS.black12 },
          },
          labels: {
            format: '{value}%',
            style: CHART_STYLES.yAxisLabelsStyleWithPcnt,
          },
        },
        {
          // Secondary Y axis for median DPI
          min: 0,
          title: {
            text: 'Median DPI',
            style: { color: CHART_COLORS.black12 },
          },
          labels: {
            format: '{value}x',
            style: CHART_STYLES.yAxisLabelsStyleWithSuffix,
          },
          opposite: true,
        },
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat: '<span style="font-size: 0.8rem; padding-bottom: 0.5rem"><b>{point.key}</b></span><br><br>',
        pointFormatter(): string {
          const colorDot = '<span style="color: ' + this.color + '">&#9679;</span>';
          return '<div style="text-align: left;">' + colorDot + ' ' + this.series.name + ': <b>'
            + this.y.toFixed(2) + (this.series.name === 'Median DPI' ? 'x' : '%') + '</b></div>';
        },
      },
      series: [
        {
          name: '% of funds with DPI',
          type: 'column',
          yAxis: 0,
          data: data.pcntOfFunds,
          color: CHART_COLORS.main8,
        },
        {
          name: 'Median DPI',
          type: 'line',
          yAxis: 1,
          data: data.medianDpi,
          color: CHART_COLORS.orange,
          marker: {
            enabled: true,
            symbol: 'circle',
          },
          dataLabels: {
            enabled: true,
            format: 'x{y:.2f}',
          },
        },
      ],
      legend: {
        ...CHART_STYLES.legend(),
        reversed: false,
      },
    };
  }


  private static getPieChartOptions(data: MaPcntPieChart[]): Partial<HighchartsOptions> {
    return {
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}:</b><br>{point.y:.1f}%',
            connectorColor: CHART_COLORS.black6,
            style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
          },
          size: '70%',
          innerSize: '60%',
          states: CHART_STYLES.pieChartStates,
        },
      },
      colors: CHART_COLORS.maRaisedByIndustry,
      tooltip: CHART_STYLES.tooltipWithPcnt(1),
      series: [{
        type: 'pie',
        name: 'Industry percentage',
        data,
      }],
    };
  }

  private static createTopFiveIndustriesChartOptions(data: MaTopFiveIndustriesChartDTO): MaTopFiveIndustriesChartsOptions {
    return {
      capitalRaised: this.createTopFiveSeriesOptions(data.capitalRaised),
      numberOfDeals: this.createTopFiveSeriesOptions(data.numberOfDeals),
    };
  }

  private static createTopFiveSeriesOptions(data: MaTopFiveIndChartsSeriesDTO): HighchartsOptions {
    return {
      xAxis: {
        type: 'category',
        categories: data.periods,
        crosshair: false,
        labels: CHART_STYLES.xAxisLabelsStyle,
      },
      yAxis: [
        {
          min: 0,
          title: {
            text: undefined,
          },
          labels: CHART_STYLES.yAxisLabelsStyleWithPcnt,
          stackLabels: CHART_STYLES.grandTotalStackLabels({ decimals: 0, includePercentage: true }),
        },
      ],
      tooltip: CHART_STYLES.maNumberOfDealsTooltip,
      legend: {
        ...CHART_STYLES.legend(),
        reversed: true,
      },
      series: data.industries.map((industry, index) => {
        const colors = [
          CHART_COLORS.vibrantMagenta,
          CHART_COLORS.orange,
          CHART_COLORS.main8,
          CHART_COLORS.green,
          CHART_COLORS.grey,
        ];

        return {
          name: industry.industryName,
          type: 'column',
          data: industry.values,
          color: colors[index],
        };
      }),
    };
  }
}
